import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Archive, RotateCcw, Trash2, Lock, Search, ShieldCheck,
  AlertCircle, ExternalLink, Library,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DELETE_REASON_CHOICES } from "@/components/DeleteRecordButton";
import { api, errMsg } from "@/lib/api";
import { useAuth, isAdmin } from "@/lib/auth";

const CATALOG_CLASSES = [
  "GFMP Internal",
  "GFMP Sync & Admin",
  "External Compliance Only",
];

function classBadge(cls) {
  const map = {
    "GFMP Internal":             { bg: "hsl(347 60% 22% / 0.10)", fg: "hsl(347 60% 22%)" },
    "GFMP Sync & Admin":         { bg: "hsl(40 48% 58% / 0.18)",  fg: "hsl(38 60% 28%)" },
    "External Compliance Only":  { bg: "hsl(225 14% 14% / 0.08)", fg: "hsl(225 14% 14%)" },
  };
  const s = map[cls] || map["External Compliance Only"];
  return (
    <Badge
      variant="outline"
      className="font-medium text-[11px]"
      style={{ background: s.bg, color: s.fg, borderColor: s.fg + "33" }}
      data-testid={`registry-class-${cls.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {cls}
    </Badge>
  );
}

function statusBadge(active) {
  return active ? (
    <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200 text-[11px]"
           data-testid="registry-status-active">Active</Badge>
  ) : (
    <Badge variant="outline" className="bg-muted text-muted-foreground border-muted-foreground/20 text-[11px]"
           data-testid="registry-status-archived">Archived</Badge>
  );
}

export default function CatalogCodeRegistry() {
  const { user } = useAuth();
  const canManage = isAdmin(user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterClass, setFilterClass] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all"); // all / active / archived
  const [q, setQ] = useState("");

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [draftCode, setDraftCode] = useState("");
  const [draftName, setDraftName] = useState("");
  const [draftNote, setDraftNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteOther, setDeleteOther] = useState("");
  const isSuperAdmin = user?.role === "super_admin";

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus === "active")    params.active_only = true;
      // archived-only is filtered client-side below for simplicity
      if (filterClass !== "all")        params.catalog_class = filterClass;
      if (q.trim())                     params.q = q.trim();
      const r = await api.get("/catalog-codes", { params });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(errMsg(err, "Could not load Catalog Code Registry."));
    } finally {
      setLoading(false);
    }
  }

  // load() depends on filterStatus/filterClass/q via closure — listing
  // them as deps is sufficient; we intentionally omit `load` itself to
  // avoid recreating the effect on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterClass, filterStatus]);
  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => load(), 280);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const visibleItems = useMemo(() => {
    if (filterStatus === "archived") return items.filter((x) => !x.is_active);
    return items;
  }, [items, filterStatus]);

  // Stats for the header
  const totals = useMemo(() => {
    const t = { all: items.length, active: 0, archived: 0, system: 0, external: 0 };
    items.forEach((i) => {
      if (i.is_active) t.active++;
      else t.archived++;
      if (i.is_system) t.system++;
      else t.external++;
    });
    return t;
  }, [items]);

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    setCreateError("");
    try {
      await api.post("/catalog-codes", {
        code: draftCode.trim().toUpperCase(),
        name: draftName.trim(),
        internal_note: draftNote.trim(),
      });
      toast.success(`Catalog Code ${draftCode.trim().toUpperCase()} created.`);
      setCreateOpen(false);
      setDraftCode(""); setDraftName(""); setDraftNote("");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        setCreateError((detail.message || "Invalid Catalog Code.") + "\n• " + detail.errors.join("\n• "));
      } else if (typeof detail === "string") {
        setCreateError(detail);
      } else {
        setCreateError(errMsg(err, "Could not create Catalog Code."));
      }
    } finally {
      setCreating(false);
    }
  }

  async function handleArchive(item) {
    try {
      await api.put(`/catalog-codes/${encodeURIComponent(item.code)}/archive`);
      toast.success(`Catalog Code ${item.code} archived.`);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "Could not archive Catalog Code."));
    }
  }

  async function handleReactivate(item) {
    try {
      await api.put(`/catalog-codes/${encodeURIComponent(item.code)}/reactivate`);
      toast.success(`Catalog Code ${item.code} reactivated.`);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "Could not reactivate Catalog Code."));
    }
  }

  async function handleDeleteConfirmed() {
    if (!deleteTarget) return;
    if (!deleteReason) return;
    if (deleteReason === "Other" && (deleteOther || "").trim().length < 3) return;
    setDeleting(true);
    try {
      const finalReason = deleteReason === "Other" ? `Other: ${deleteOther.trim()}` : deleteReason;
      const qs = new URLSearchParams();
      qs.set("reason", finalReason);
      if (isSuperAdmin) qs.set("force", "true");
      await api.delete(`/catalog-codes/${encodeURIComponent(deleteTarget.code)}?${qs.toString()}`);
      toast.success(`Catalog Code ${deleteTarget.code} deleted.`);
      setDeleteTarget(null);
      setDeleteReason(""); setDeleteOther("");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.linked_tracks > 0) {
        toast.error(
          `Cannot delete ${deleteTarget.code}: ${detail.linked_tracks} linked track(s). Archive it instead.`,
          { duration: 6000 }
        );
      } else {
        toast.error(errMsg(err, "Could not delete Catalog Code."));
      }
    } finally {
      setDeleting(false);
    }
  }

  function fmtDate(iso) {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
    } catch { return iso; }
  }

  return (
    <div className="space-y-6" data-testid="catalog-code-registry-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
            Internal · Compliance
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight flex items-center gap-3">
            <Library className="w-7 h-7" style={{ color: "hsl(347 60% 22%)" }} />
            Catalog Code Registry
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Every track in Kandake belongs to a Catalog Code. Use this registry to manage
            GFMP-controlled system codes and to define external compliance-only catalogues.
          </p>
        </div>
        {canManage && (
          <Button
            onClick={() => { setCreateError(""); setCreateOpen(true); }}
            data-testid="registry-create-button"
            style={{ background: "hsl(347 60% 22%)" }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create External Catalog Code
          </Button>
        )}
      </div>

      {!canManage && (
        <Alert data-testid="registry-readonly-banner">
          <ShieldCheck className="w-4 h-4" />
          <AlertDescription>
            You can view the registry. Only the Super Admin may create, archive,
            reactivate, or delete Catalog Codes.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="registry-stats">
        <StatTile label="Total"    value={totals.all}      testid="registry-stat-total" />
        <StatTile label="Active"   value={totals.active}   testid="registry-stat-active" />
        <StatTile label="Archived" value={totals.archived} testid="registry-stat-archived" />
        <StatTile label="System"   value={totals.system}   testid="registry-stat-system" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs">Search</Label>
              <div className="relative mt-1">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Code or catalogue name…"
                  className="pl-9"
                  data-testid="registry-search-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Class</Label>
              <Select value={filterClass} onValueChange={setFilterClass}>
                <SelectTrigger className="w-[220px] mt-1" data-testid="registry-filter-class">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All classes</SelectItem>
                  {CATALOG_CLASSES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[160px] mt-1" data-testid="registry-filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active only</SelectItem>
                  <SelectItem value="archived">Archived only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table data-testid="registry-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Code</TableHead>
                <TableHead>Catalogue Name</TableHead>
                <TableHead className="w-[180px]">Class</TableHead>
                <TableHead className="w-[110px]">Status</TableHead>
                <TableHead className="w-[110px] text-right">Tracks</TableHead>
                <TableHead className="w-[140px]">Created</TableHead>
                <TableHead className="w-[170px]">Created By</TableHead>
                {canManage && <TableHead className="w-[140px] text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={canManage ? 8 : 7} className="text-center text-sm text-muted-foreground py-8">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && visibleItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canManage ? 8 : 7} className="text-center text-sm text-muted-foreground py-8"
                             data-testid="registry-empty">
                    No Catalog Codes match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading && visibleItems.map((item) => (
                <TableRow key={item.id} data-testid={`registry-row-${item.code}`}>
                  <TableCell className="font-mono font-medium">
                    <div className="inline-flex items-center gap-1.5">
                      {item.is_system && (
                        <Lock className="w-3 h-3 text-muted-foreground" aria-label="System protected" />
                      )}
                      {item.code}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{item.name}</div>
                    {item.internal_note && (
                      <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
                        {item.internal_note}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{classBadge(item.catalog_class)}</TableCell>
                  <TableCell>{statusBadge(item.is_active)}</TableCell>
                  <TableCell className="text-right font-mono text-sm" data-testid={`registry-track-count-${item.code}`}>
                    {item.track_count ?? 0}
                  </TableCell>
                  <TableCell className="text-[12px]">{fmtDate(item.created_at)}</TableCell>
                  <TableCell className="text-[12px]">
                    <div className="font-medium">{item.created_by_name || "—"}</div>
                    {item.created_by_email && (
                      <div className="text-muted-foreground text-[10px] truncate max-w-[150px]" title={item.created_by_email}>
                        {item.created_by_email}
                      </div>
                    )}
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {item.is_system ? (
                          <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1" title="System code — protected">
                            <Lock className="w-3 h-3" />
                            Protected
                          </span>
                        ) : item.is_active ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleArchive(item)}
                              data-testid={`registry-archive-${item.code}`}
                              title="Archive — hides from new-track picker but preserves existing track links"
                            >
                              <Archive className="w-3.5 h-3.5 mr-1" />
                              Archive
                            </Button>
                            {item.track_count === 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteTarget(item)}
                                data-testid={`registry-delete-${item.code}`}
                                title="Delete — only allowed for codes with zero linked tracks"
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReactivate(item)}
                              data-testid={`registry-reactivate-${item.code}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5 mr-1" />
                              Reactivate
                            </Button>
                            {item.track_count === 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteTarget(item)}
                                data-testid={`registry-delete-${item.code}`}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="text-[11px] text-muted-foreground">
        <Link to="/tracks" className="inline-flex items-center gap-1 hover:underline" data-testid="registry-back-to-tracks">
          <ExternalLink className="w-3 h-3" />
          Back to Catalog / Tracks
        </Link>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="registry-create-dialog">
          <DialogHeader>
            <DialogTitle>Create External Catalog Code</DialogTitle>
            <DialogDescription>
              External compliance-only catalogues are catalogues not under GFMP publishing,
              Sync &amp; Admin, ownership, or control. The code is normalised to uppercase.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4" data-testid="registry-create-form" autoComplete="off">
            <div className="space-y-1.5">
              <Label htmlFor="cc-code">Catalog Code *</Label>
              <Input
                id="cc-code"
                value={draftCode}
                onChange={(e) => setDraftCode(e.target.value.toUpperCase())}
                placeholder="e.g. EJE"
                maxLength={32}
                className="font-mono uppercase tracking-wide"
                spellCheck={false}
                data-testid="registry-create-code"
                required
              />
              <p className="text-[11px] text-muted-foreground">
                Uppercase letters, numbers, and hyphens only. The <code className="font-mono">GFM-</code> prefix
                is reserved for GFMP system codes.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cc-name">Catalogue Name *</Label>
              <Input
                id="cc-name"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder="e.g. Esmond Jerry Elcock"
                maxLength={200}
                data-testid="registry-create-name"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cc-note">Internal note (optional)</Label>
              <Textarea
                id="cc-note"
                value={draftNote}
                onChange={(e) => setDraftNote(e.target.value)}
                placeholder="Internal context for compliance reviewers (never displayed publicly)"
                maxLength={1000}
                rows={3}
                data-testid="registry-create-note"
              />
            </div>
            <div className="text-[11px] text-muted-foreground bg-secondary/40 px-3 py-2 rounded-md">
              Class will be set to <span className="font-medium text-foreground">External Compliance Only</span>.
            </div>
            {createError && (
              <Alert variant="destructive" data-testid="registry-create-error">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription className="whitespace-pre-wrap">{createError}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}
                      data-testid="registry-create-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={creating || !draftCode.trim() || !draftName.trim()}
                      data-testid="registry-create-submit"
                      style={{ background: "hsl(347 60% 22%)" }}>
                {creating ? "Creating…" : "Create Catalog Code"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteReason(""); setDeleteOther(""); } }}>
        <AlertDialogContent data-testid="registry-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Catalog Code {deleteTarget?.code}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action is permanent. {deleteTarget?.code} has{" "}
              <span className="font-mono">{deleteTarget?.track_count ?? 0}</span> linked track(s).
              {deleteTarget?.track_count > 0 && !isSuperAdmin && (
                <span className="block mt-2 text-destructive font-medium">
                  Deletion is blocked because tracks reference this code. Archive instead.
                </span>
              )}
              {deleteTarget?.track_count > 0 && isSuperAdmin && (
                <span className="block mt-2 text-destructive font-medium">
                  Super Admin cascade: linked tracks will retain their code reference as an audit
                  artefact. Consider archiving instead.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Reason (required · captured in audit)
              </Label>
              <Select value={deleteReason} onValueChange={setDeleteReason}>
                <SelectTrigger className="mt-1 text-[12.5px]" data-testid="registry-delete-reason-trigger">
                  <SelectValue placeholder="Select a reason…" />
                </SelectTrigger>
                <SelectContent>
                  {DELETE_REASON_CHOICES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {deleteReason === "Other" && (
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Explanation (required when reason is Other)
                </Label>
                <Textarea
                  value={deleteOther}
                  onChange={(e) => setDeleteOther(e.target.value)}
                  rows={2}
                  className="mt-1 text-[12.5px]"
                  data-testid="registry-delete-reason-other"
                />
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="registry-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirmed}
              disabled={deleting || !deleteReason || (deleteReason === "Other" && (deleteOther || "").trim().length < 3) || (!isSuperAdmin && (deleteTarget?.track_count ?? 0) > 0)}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="registry-delete-confirm"
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatTile({ label, value, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
        <div className="font-display text-2xl mt-0.5">{value}</div>
      </CardContent>
    </Card>
  );
}
